// Copyright 2025 - Hewlett Packard Enterprise Company
import React, { createContext, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { postQuery, getChatAsync } from '../actions/Api';

// Create the ChatContext
export const ChatContext = createContext();

// ChatProvider component
export function ChatProvider({ children }) {
  const [conversations, setConversations] = useState([]);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const addConversation = useCallback(query => {
    setConversations(prevConversations => [
      ...prevConversations,
      {
        query,
        answer: 'Loading',
        showFollowUpQuestions: true,
        showFeedback: true,
      },
    ]);
  }, []);

  const updateConversation = useCallback((index, answer) => {
    setConversations(prevConversations => {
      const updatedConversations = [...prevConversations];
      if (index >= 0 && index < updatedConversations.length) {
        updatedConversations[index] = {
          ...updatedConversations[index],
          answer,
          showFollowUpQuestions: answer?.followupQuestions?.length > 0,
          showFeedback: true,
        };
        return updatedConversations;
      }
      return prevConversations;
    });
  }, []);

  const clearConversations = useCallback(() => {
    setConversations([]);
  }, []);

  const hideAllFollowUpQuestions = useCallback(() => {
    setConversations(prevConversations =>
      prevConversations.map(conv => ({
        ...conv,
        showFollowUpQuestions: false,
      })),
    );
  }, []);

  const hideAllFeedback = useCallback(() => {
    setConversations(prevConversations =>
      prevConversations.map(conv => ({
        ...conv,
        showFeedback: false,
      })),
    );
  }, []);

  const getAsyncResponse = useCallback(async id => {
    const maxRetries = 9;
    const pollingInterval = 5000; // 5 seconds

    let answer = null;

    for (let i = 0; i < maxRetries; i++) {
      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise(r => setTimeout(r, pollingInterval));

      try {
        // eslint-disable-next-line no-await-in-loop
        const chatResponse = await getChatAsync(id);

        if (
          chatResponse?.data &&
          chatResponse.data.summary !==
            'Chat response is being processed asynchronously.'
        ) {
          answer = chatResponse?.data;
          break;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error during polling:', error);
        break; // Exit the loop on error
      }
    }

    if (!answer) {
      throw new Error('Polling timed out');
    }

    return answer;
  }, []);

  const handleQueryClick = useCallback(
    async query => {
      const index = conversations.length;
      addConversation(query);
      setIsFeedbackOpen(false);
      try {
        const response = await postQuery(query);
        const id = response?.data?.id;
        const answer = await getAsyncResponse(id);

        updateConversation(index, answer);
      } catch (error) {
        updateConversation(index, 'Error retrieving answer');
      }
    },
    [conversations, addConversation, updateConversation, getAsyncResponse],
  );

  // Use useMemo to memoize the context value
  const contextValue = useMemo(
    () => ({
      conversations,
      addConversation,
      updateConversation,
      clearConversations,
      handleQueryClick,
      hideAllFollowUpQuestions,
      getAsyncResponse,
      isFeedbackOpen,
      setIsFeedbackOpen,
      hideAllFeedback,
    }),
    [
      conversations,
      addConversation,
      updateConversation,
      clearConversations,
      handleQueryClick,
      hideAllFollowUpQuestions,
      getAsyncResponse,
      isFeedbackOpen,
      setIsFeedbackOpen,
      hideAllFeedback,
    ],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
